import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header/Header';
import { ChartActionBar } from '@/components/Chart/ChartActionBar';
import { DrawingToolbar } from '@/components/Chart/DrawingToolbar';
import { RightToolbar } from '@/components/Layout/RightToolbar';
import { IBDGrowthWidget } from '@/components/Chart/IBDGrowthWidget';
import { MacroBar } from '@/components/Macro/MacroBar';
import { useChartStore } from '@/store/chartStore';
import { useAlertStore } from '@/store/alertStore';
import { useAuthStore } from '@/store/authStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useMcpOnboarding } from '@/hooks/useMcpOnboarding';
import { useNudgeTimer } from '@/hooks/useNudgeTimer';
import { WatchlistIcon, LayoutIcon, BellIcon, BotIcon, ScreenerIcon } from '@/components/icons';
import { FooterStrip } from './SiteFooter';

// None of these are needed for first paint of the chart page — they're only
// ever mounted behind a boolean toggle — so they're split into their own
// chunks instead of bloating the main bundle.
const WatchlistPanel = lazy(() =>
  import('@/components/Watchlist/WatchlistPanel').then((m) => ({ default: m.WatchlistPanel })),
);
const IndicatorTemplatePanel = lazy(() =>
  import('@/components/Chart/IndicatorTemplatePanel').then((m) => ({ default: m.IndicatorTemplatePanel })),
);
const AIChatPanel = lazy(() =>
  import('@/components/AIChat/AIChatPanel').then((m) => ({ default: m.AIChatPanel })),
);
const AlertsPanel = lazy(() =>
  import('@/components/Alerts/AlertsPanel').then((m) => ({ default: m.AlertsPanel })),
);
const StockScreener = lazy(() => import('@/components/Screener2/StockScreener'));
const FundamentalsModal = lazy(() =>
  import('@/components/Fundamentals/FundamentalsModal').then((m) => ({ default: m.FundamentalsModal })),
);
const McpOnboardingModal = lazy(() =>
  import('@/components/MCP/McpOnboardingModal').then((m) => ({ default: m.McpOnboardingModal })),
);
const NudgeModal = lazy(() =>
  import('@/components/Nudges/NudgeModal').then((m) => ({ default: m.NudgeModal })),
);

const PANELS = [
  { id: 'watchlist'     as const, icon: WatchlistIcon,     title: 'Watchlist'     },
  { id: 'templates'     as const, icon: LayoutIcon,         title: 'Templates'     },
  { id: 'alerts'        as const, icon: BellIcon,           title: 'Alerts'        },
  { id: 'ai'            as const, icon: BotIcon,            title: 'SerenAI'       },
  { id: 'screener'      as const, icon: ScreenerIcon,       title: 'Screener'      },
];

function ResendVerifyButton() {
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [error,    setError]    = useState('');

  async function handleResend() {
    setSending(true); setSent(false); setError('');
    try {
      const res = await fetch('/api/auth/resend-verify', { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        const j = (await res.json()) as { error?: string };
        throw new Error(j.error ?? 'Failed to resend');
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSending(false);
    }
  }

  if (sent) return <p className="text-xs text-emerald-400 text-center">Verification email resent. Check your inbox.</p>;
  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={handleResend}
        disabled={sending}
        className="w-full py-2.5 rounded-xl bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
      >
        {sending ? 'Sending…' : 'Resend verification email'}
      </button>
      {error && <p className="text-xs text-red-400 text-center">{error}</p>}
    </div>
  );
}

// Inline "change email" for the verification hard-gate — the gate sits above
// everything else (z-critical-overlay), so opening Settings from here would
// just render behind it and be unreachable. This lets the user fix a typo'd
// email without leaving the gate.
function ChangeEmailInline() {
  const user       = useAuthStore((s) => s.user);
  const changeEmail = useAuthStore((s) => s.changeEmail);

  const [open,     setOpen]     = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-xs text-accent hover:underline">
        {user?.pendingEmail ? 'Sent to the wrong address? Change it again' : 'Wrong email? Change it'}
      </button>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await changeEmail(newEmail.trim(), password || undefined);
      // Collapse back to the link — the gate's own message (driven by
      // user.pendingEmail) now shows the confirmation instead.
      setOpen(false);
      setNewEmail('');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change email');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 text-left">
      <input
        type="email"
        value={newEmail}
        onChange={(e) => setNewEmail(e.target.value)}
        placeholder="new@example.com"
        required
        autoFocus
        className="w-full px-3 py-2 rounded-lg bg-bg border border-border text-text text-xs placeholder:text-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
      />
      {!user?.isGoogleAccount && (
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Current password"
          className="w-full px-3 py-2 rounded-lg bg-bg border border-border text-text text-xs placeholder:text-muted focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
        />
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="flex-1 py-2 rounded-lg bg-accent text-white text-xs font-medium hover:bg-accent/90 transition-colors disabled:opacity-50"
        >
          {saving ? 'Sending…' : 'Send verification link'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="px-3 py-2 rounded-lg text-xs text-muted hover:text-text transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export function AppLayout() {
  const rightToolbarOpen    = useChartStore((s) => s.rightToolbarOpen);
  const toggleRightToolbar  = useChartStore((s) => s.toggleRightToolbar);
  const rightPanelActive    = useChartStore((s) => s.rightPanelActive);
  const setRightPanelActive = useChartStore((s) => s.setRightPanelActive);
  const fetchAlerts         = useAlertStore((s) => s.fetchAlerts);
  const symbol              = useChartStore((s) => s.symbol);

  const user               = useAuthStore((s) => s.user);
  const logout             = useAuthStore((s) => s.logout);
  const openUpgradeModal   = useAuthStore((s) => s.openUpgradeModal);

  const [screenerOpen, setScreenerOpen] = useState(false);
  const [fundamentalsOpen, setFundamentalsOpen] = useState(false);
  const { isOpen: mcpOnboardingOpen, close: closeMcpOnboarding } = useMcpOnboarding();
  const { nudgeType, dismiss: dismissNudge, optOut: optOutNudges } = useNudgeTimer();
  const navigate = useNavigate();
  const isProTier = user?.plan === 'pro' || user?.plan === 'business';

  const isMobile = useIsMobile();

  // The Financials modal's full statements/shareholder data requires a
  // session (see functions/_middleware.ts) — send anonymous visitors to
  // login instead of opening a modal that will just show empty/401'd tabs.
  function openFullFinancials() {
    if (!user) { navigate('/login'); return; }
    setFundamentalsOpen(true);
  }

  // Fetch alerts once a session is confirmed, so chart overlay lines persist
  // regardless of panel state. Gated on `user` — anonymous visitors have no
  // alerts, and calling this unconditionally guaranteed a 401 on every load.
  useEffect(() => {
    if (!user) return;
    fetchAlerts();
  }, [fetchAlerts, user]);

  // On mobile (mount or transition to mobile), close any open right panel and
  // collapse drawing toolbar so the layout doesn't break.
  const hasClosedForMobileRef = useRef(false);
  useEffect(() => {
    if (isMobile && !hasClosedForMobileRef.current) {
      const state = useChartStore.getState();
      if (state.rightToolbarOpen) state.toggleRightToolbar();
      state.setDrawingToolbarCollapsed(true);
      hasClosedForMobileRef.current = true;
    }
    if (!isMobile) {
      hasClosedForMobileRef.current = false;
    }
  }, [isMobile]);

  function toggleMobilePanel(panel: 'watchlist' | 'templates' | 'ai' | 'alerts' | 'screener') {
    if (panel === 'screener') {
      setScreenerOpen(s => !s);
      return;
    }
    const isCurrentlyOpen = rightToolbarOpen && rightPanelActive === panel;
    if (isCurrentlyOpen) {
      setRightPanelActive(null);
      toggleRightToolbar(); // close
    } else {
      setRightPanelActive(panel);
      if (!rightToolbarOpen) toggleRightToolbar(); // open if not already
    }
  }

  const mobileDrawerOpen = isMobile && rightToolbarOpen && rightPanelActive !== null;

  return (
    <div className={`flex flex-col h-full overflow-hidden app-bg text-text select-none relative ${isMobile ? 'pb-[104px]' : ''}`}>
      <Header />

      {/* Macro indicator strip — shown when at least one series is pinned */}
      <MacroBar />

      <div className="flex flex-1 overflow-hidden">
        <DrawingToolbar />

        <main className="flex-1 overflow-hidden relative">
          <Outlet />
        </main>

        {/* Desktop-only: inline right panels + toolbar strip */}
        <Suspense fallback={null}>
          {!isMobile && rightToolbarOpen && rightPanelActive === 'watchlist'  && <WatchlistPanel />}
          {!isMobile && rightToolbarOpen && rightPanelActive === 'templates'  && <IndicatorTemplatePanel />}
          {!isMobile && rightToolbarOpen && rightPanelActive === 'ai'         && <AIChatPanel />}
          {!isMobile && rightToolbarOpen && rightPanelActive === 'alerts'     && <AlertsPanel />}
        </Suspense>
        {!isMobile && rightToolbarOpen && (
          <RightToolbar
            onOpenScreener={() => setScreenerOpen(true)}
            onOpenFullFinancials={openFullFinancials}
          />
        )}
      </div>

      {/* Desktop: one-line attribution + site links. On mobile the bottom of the
          screen is already the action bar and nav bar, so the same links live in
          the chart settings modal instead. */}
      {!isMobile && <FooterStrip />}

      {/* Mobile: fixed bottom container (action bar + nav) */}
      {isMobile && (
        <div className="fixed bottom-0 inset-x-0 z-panel-backdrop">
          <ChartActionBar />
          <nav
            className="h-14 border-t flex items-center justify-around shrink-0"
            style={{
              background: 'var(--glass-header)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderTopColor: 'var(--glass-border)',
              boxShadow: 'var(--glass-shadow)',
            }}
          >
            {PANELS.map(({ id, icon: Icon, title }) => {
              const active = rightToolbarOpen && rightPanelActive === id;
              return (
                <button
                  key={id}
                  onClick={() => toggleMobilePanel(id)}
                  title={title}
                  className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-xs transition-colors ${
                    active ? 'text-accent' : 'text-muted'
                  }`}
                >
                  <Icon size={22} />
                  <span className="text-[11px] leading-none">{title}</span>
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* IBD Growth floating widget */}
      <IBDGrowthWidget onOpenFullFinancials={openFullFinancials} />

      {/* Screener full-page modal */}
      {screenerOpen && (
        <Suspense fallback={null}>
          <StockScreener onClose={() => setScreenerOpen(false)} />
        </Suspense>
      )}

      {/* Fundamentals full-page modal */}
      {fundamentalsOpen && (
        <Suspense fallback={null}>
          <FundamentalsModal
            initialSymbol={symbol}
            onClose={() => setFundamentalsOpen(false)}
            onUpgrade={openUpgradeModal}
          />
        </Suspense>
      )}

      {/* Email verification hard gate — stays up until email_verified is actually
          true. Requesting an email change while unverified only sets pendingEmail;
          it must NOT be treated as satisfying verification (swapPendingEmail() is
          what flips email_verified, once the NEW address's link is clicked). */}
      {user && !user.emailVerified && (
        <div className="fixed inset-0 z-critical-overlay bg-bg/95 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-sm bg-panel border border-border rounded-2xl shadow-2xl p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold text-text">Verify your email</h2>
              {user.pendingEmail ? (
                <p className="text-xs text-muted leading-relaxed">
                  We sent a confirmation link to <strong className="text-text">{user.pendingEmail}</strong>. Please check your inbox and click the link to continue.
                </p>
              ) : (
                <p className="text-xs text-muted leading-relaxed">
                  We sent a verification link to <strong className="text-text">{user.email}</strong>. Please check your inbox and click the link to continue.
                </p>
              )}
            </div>

            {!user.pendingEmail && <ResendVerifyButton />}

            <div className="flex flex-col gap-2 text-center">
              <ChangeEmailInline />
              <button
                onClick={() => logout()}
                className="text-xs text-muted hover:text-red-400 transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MCP onboarding modal — shown once per browser */}
      {mcpOnboardingOpen && (
        <Suspense fallback={null}>
          <McpOnboardingModal onClose={closeMcpOnboarding} />
        </Suspense>
      )}

      {/* Periodic sign-in / upgrade / feature-discovery nudge */}
      {nudgeType && (
        <Suspense fallback={null}>
          <NudgeModal
            variant={nudgeType}
            allowOptOut={isProTier}
            onClose={(dontShowAgain) => {
              if (dontShowAgain) optOutNudges();
              dismissNudge();
            }}
            onCta={(dontShowAgain) => {
              if (dontShowAgain) optOutNudges();
              dismissNudge();
              if (nudgeType === 'signin') {
                navigate('/login');
              } else if (nudgeType === 'pro') {
                openUpgradeModal();
              } else if (nudgeType === 'alerts') {
                setRightPanelActive('alerts');
                if (!rightToolbarOpen) toggleRightToolbar();
              } else if (nudgeType === 'screening') {
                setScreenerOpen(true);
              } else if (nudgeType === 'financials') {
                openFullFinancials();
              }
            }}
          />
        </Suspense>
      )}

      {/* Mobile: drawer panel with backdrop */}
      {mobileDrawerOpen && (
        <>
          {/* Backdrop — only covers area above the nav so tapping the nav goes directly to toggleMobilePanel */}
          <div
            className="fixed top-0 left-0 right-0 bottom-[104px] z-drawer-backdrop bg-black/50"
            onPointerDown={() => {
              setRightPanelActive(null);
              if (rightToolbarOpen) toggleRightToolbar();
            }}
          />
          {/* Drawer */}
          <div className="fixed right-0 top-0 bottom-[104px] z-dropdown flex shadow-2xl">
            <Suspense fallback={null}>
              {rightPanelActive === 'watchlist'  && <WatchlistPanel />}
              {rightPanelActive === 'templates'  && <IndicatorTemplatePanel />}
              {rightPanelActive === 'ai'         && <AIChatPanel />}
              {rightPanelActive === 'alerts'     && <AlertsPanel />}
            </Suspense>
          </div>
        </>
      )}
    </div>
  );
}
